import React, { ReactNode, useContext, useLayoutEffect, useState } from 'react'
import { TFunction, useTranslation } from 'react-i18next'
import { ErrorResponse, isErrorResponse } from '../types/api'
import { Feature, ID, Location, User } from '../types/models'
import { useLocations } from './useLocations'
import { useUser } from './useUser'
import { Safety } from '../classes/Safety'

/**
 * This module provides global context and related types for the application.
 */

/**
 * Severity of the message.
 */
export type MessageType = 'error' | 'warning' | 'info' | 'announcement'

/**
 * Content of the message.
 */
export type Message = {
  type: MessageType
  text: string
  url?: string
}

/**
 * Global application context object.
 */
export interface AppContextProps {
  // Data
  alert: string | null
  messages: Message[]
  user: User | null
  locations: Location[]
  installerLevel: boolean
  mobileWidth: number

  // Functions
  addInfo: (text: string) => void
  addWarning: (text: string) => void
  addError: (text: string | ErrorResponse) => void
  setUser: (user: User | null) => void
  setLocations: (locations: Location[]) => void
  setInstallerLevel: (isInstallerLevel: boolean) => void
  t: TFunction<'translation', undefined>
  userHas: (feature: Feature) => boolean
  setAlert: (text: string) => void
}

/**
 * Placeholder initializer for app context.
 */
export const emptyAppContext = {
  alert: null,
  messages: [],
  user: null,
  locations: [],
  installerLevel: false,
  mobileWidth: window.innerWidth,

  setInstallerLevel: () => {},
  addInfo: () => {},
  addWarning: () => {},
  addError: () => {},
  setUser: () => {},
  setLocations: () => {},
  t: () => '',
  userHas: () => false,
  setAlert: () => {},
}

/**
 * An instance of the global context.
 */
export const AppContext = React.createContext<AppContextProps>(emptyAppContext)

/**
 * Helper hook to access global context.
 */
export const useApp = () => useContext(AppContext)

/**
 * Provider for the global context.
 */
export const AppProvider = (props: { children: ReactNode }): JSX.Element => {

  const [alert, setAlert] = useState<string | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [installerLevel, setInstallerLevel] = useState<boolean>(false)
  const [user, setUser] = useUser()
  const [timer, setTimer] = useState<null|NodeJS.Timeout>(null)
  const [locations, setLocations] = useLocations()
  const [mobileWidth, setMobileWidth] = useState<number>(window.innerWidth)
  const { t } = useTranslation()

  useLayoutEffect(() => {
    const installerLevel = Safety.get('installer-level')
    if (installerLevel) {
      setInstallerLevel(true)
    }
  }, [])

  useLayoutEffect(() => {
    window.addEventListener('resize', handleMobileSizeChange)
    return () => {
      window.removeEventListener('resize', handleMobileSizeChange)
    }
  }, [])

  const handleMobileSizeChange = () => {
    setMobileWidth(window.innerWidth)
  }

  // Fill in locations for devices.
  const locationMap: Record<ID, Location> = {}

  for (const location of locations) {
    if (location.id) {
      locationMap[location.id] = location
    }
  }

  // Define functions for interface.
  const addMessage = (type: MessageType, text: string) => {
    if (timer) clearTimeout(timer)

    setMessages((messages) => messages.concat({ type, text }))

    setTimer(setTimeout(() => {
      setMessages([])
    }, 6000))
  }

  const addInfo = (text: string) => {
    addMessage('info', text)
  }

  const addWarning = (text: string) => {
    addMessage('warning', text)
  }

  const addError = (error: string | ErrorResponse) => {
    if (isErrorResponse(error)) {
      Object.entries(error.messages).forEach(([name, errors]) => {
        errors.forEach((err: string|string[]) => {
          addError(`${err instanceof Array ? err.join('; ') : err}`)
        })
      })
    } else {
      addMessage('error', error)
    }
  }

  const userHas = (feature: Feature): boolean => {
    return !!(user && user.features && user.features.includes(feature))
  }

  return (
    <AppContext.Provider value={{
      alert,
      messages,
      user,
      locations,
      installerLevel,
      mobileWidth,
      addInfo,
      addWarning,
      addError,
      setUser,
      setLocations,
      t,
      userHas,
      setInstallerLevel,
      setAlert,
    }}>
      {props.children}
    </AppContext.Provider>
  )
}
